/**
 * 65.16 — the strike drone (owner, Oct 10: "kamikaze grenade variant of drone, drone controls and tasking is pretty good").
 *
 * The scout drone as it is — taken from a person, tasked on the tablet, flown along a route, brought down by one hit —
 * carrying one HE charge instead of eyes worth keeping. Tasking gains one verb, **strike**: a known enemy unit or a point
 * on the operator's planet. Launched with a strike, it flies its route and leaves it when its aim comes within its sight
 * radius of the ground under it (or at the route's end); sent a strike in the air, it leaves its route on the next tick. Diving,
 * it flies straight at its aim at STRIKE_DRONE.diveSpeed × the drone's speed — retargeting a unit it can see (its own
 * line to the unit's eye) to where the unit is now — and bursts on contact (STRIKE_DRONE.contactPeople person heights of
 * its aim or of a living enemy), or on the ground, in the person-family burst (Match runs core/rocket.ts rocketBlast with
 * STRIKE_DRONE.blast). It is spent in the blast (`end: 'struck'`); it can be shot down on the way like any drone.
 *
 * The fairness rule (the awareness core): a unit can be struck only when the team knows it — in the team's picture
 * (core/picture.ts: its shared tracks and its instruments' sightings), at a teammate bot's last look, or in the operator's
 * own sight. A point needs no knowledge (a guess is a guess). Pure and deterministic (dmath only).
 */
import { explosionBlast } from './explosions';
import type { DroneState, GameState, PlayerState, StrikeTarget, StrikeTask, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, STRIKE_DRONE } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos } from './dmath';
import { getSurfaceRadius } from './surface';
import { personSetOf } from './scaleProfile';
import { personEyePoint, personLineOfSight } from './fineTerrain';
import { hitboxCenter } from './bullets';
import { teamPicture } from './picture';
import { droneGroundDir, dronePos, legLengths, type LaunchCheck } from './drones';
import { brainFace } from './brainFace';

const angle = (a: Vec3, b: Vec3): number => dacos(Math.max(-1, Math.min(1, vecDot(a, b))));

/** The burst's numbers in world units: the rocket's blast at this scale (core/weapons.ts converts the rocket's the same way). */
export function strikeBlast(state: Pick<GameState, 'config'>): { radius: number; damage: number } {
  return explosionBlast(state, STRIKE_DRONE.explosion)!;   // 65.35: its class's
}

/** Where the team believes enemy `unitId` is, or null when it does not know it (the fairness rule; see the header). */
export function knownUnitPos(state: GameState, operator: PlayerState, unitId: number, tick: number): Vec3 | null {
  const enemy = state.players.find(p => p.id === unitId);
  if (!enemy || enemy.teamIndex === operator.teamIndex) return null;
  const ps = personSetOf(state);
  // the operator's own eyes, then a teammate bot's last look (where the enemy is now: it is in sight)
  if (enemy.alive && enemy.planetIndex === operator.planetIndex) {
    const eye = personEyePoint(operator, state.planets, ps, ps.losEyeHeight);
    const target = personEyePoint(enemy, state.planets, ps, ps.losEyeHeight);
    if (personLineOfSight(eye, target, state)) return hitboxCenter(enemy, state.planets, ps.hitboxCenter, ps);
    for (const mate of state.players) {
      if (!mate.alive || mate.teamIndex !== operator.teamIndex || mate.id === operator.id) continue;
      if (brainFace(state, mate.id)?.seenIds?.includes(unitId)) return hitboxCenter(enemy, state.planets, ps.hitboxCenter, ps);
    }
  }
  // the team's picture: its tracks and its instruments (where it was believed to be, as old as it is)
  const belief = teamPicture(state, operator.teamIndex, tick).find(b => b.unitId === unitId && b.planetIndex === operator.planetIndex);
  return belief ? { ...belief.pos } : null;
}

/** The ground at a unit direction `dir` on planet `planetIndex` (world space). */
function groundAt(state: GameState, planetIndex: number, dir: Vec3): Vec3 {
  const planet = state.planets[planetIndex];
  const d = vecNormalize(dir);
  return vecAdd(planet.position, vecScale(d, getSurfaceRadius(planet, d)));
}

/** May `operator` send a strike at `target` (with a strike drone it carries, or one of its in the air)? The task when it may. */
export function strikeTaskFor(state: GameState, operator: PlayerState, target: StrikeTarget, tick: number): { ok: true; task: StrikeTask } | (LaunchCheck & { ok: false }) {
  if ('unitId' in target) {
    const aim = knownUnitPos(state, operator, target.unitId, tick);
    if (!aim) return { ok: false, reason: 'The team does not know that unit', code: 'unknown target' };
    return { ok: true, task: { unitId: target.unitId, aim } };
  }
  const d = vecLength(target.dir);
  if (!(d > 0)) return { ok: false, reason: 'No point to strike', code: 'no target' };
  return { ok: true, task: { aim: groundAt(state, operator.planetIndex, target.dir) } };
}

/** The live strike drone `operatorId` flies that is not yet diving (the newest), or null. */
export function strikeDroneInFlight(state: GameState, operatorId: number): DroneState | null {
  let best: DroneState | null = null;
  for (const d of state.drones ?? []) if (d.alive && d.variant === 'strike' && d.operatorId === operatorId && !d.dive && (!best || d.id > best.id)) best = d;
  return best;
}

/** Send strike drone `droneId` (in the air) at `task` now: it leaves its route on the next tick. */
export function sendStrike(state: GameState, droneId: number, task: StrikeTask): GameState {
  return { ...state, drones: (state.drones ?? []).map(d => d.id === droneId && d.alive ? { ...d, strike: { ...task, now: true } } : d) };
}

/** A strike drone's burst, for Match to resolve (rocketBlast) and to report. */
export interface StrikeBurst { droneId: number; operatorId: number; point: Vec3; planetIndex: number }

/**
 * One tick of the strike drones (after `stepDrones` has flown the routes): a tasked drone whose aim is in reach of the
 * ground under it, or whose route is flown, starts its dive; a diving drone retargets a unit it sees and closes on its aim;
 * one in contact (or into the ground) bursts and is spent. Returns the bursts in drone order.
 */
export function stepStrikeDrones(state: GameState, dt: number): { state: GameState; bursts: StrikeBurst[]; dived: DroneState[] } {
  if (!(state.drones ?? []).some(d => d.alive && d.variant === 'strike' && d.strike)) return { state, bursts: [], dived: [] };
  const ps = personSetOf(state);
  const contact = STRIKE_DRONE.contactPeople * PERSON_BODY_HEIGHT * ps.k;
  const speed = ps.droneSpeed * STRIKE_DRONE.diveSpeed;
  const bursts: StrikeBurst[] = [];
  const dived: DroneState[] = [];
  const drones = (state.drones ?? []).map(d => {
    if (!d.alive || d.variant !== 'strike' || !d.strike) return d;
    const planet = state.planets[d.planetIndex];
    if (!d.dive) {
      // on its route: dive when its aim is within its sight radius of the ground under it, or the route is flown
      const ground = droneGroundDir(d, planet.radius);
      const aimDir = vecNormalize(vecSub(d.strike.aim, planet.position));
      const total = legLengths(d.route, planet.radius).reduce((a, b) => a + b, 0);
      if (!d.strike.now && angle(ground, aimDir) * planet.radius > ps.droneSightRadius && d.flown < total) return d;
      const diving = { ...d, dive: { pos: dronePos(d, state) } };
      dived.push(diving);
      return diving;
    }
    // diving: a unit it can see is where it is now
    let aim = d.strike.aim;
    const unit = d.strike.unitId !== undefined ? state.players.find(p => p.id === d.strike!.unitId) : undefined;
    if (unit?.alive && unit.planetIndex === d.planetIndex) {
      const seen = personEyePoint(unit, state.planets, ps, ps.losEyeHeight);
      if (personLineOfSight(d.dive.pos, seen, state)) aim = hitboxCenter(unit, state.planets, ps.hitboxCenter, ps);
    }
    const to = vecSub(aim, d.dive.pos);
    const dist = vecLength(to);
    const step = speed * dt;
    const pos = dist <= step ? { ...aim } : vecAdd(d.dive.pos, vecScale(to, step / dist));
    // contact: its aim, a living enemy's body, or the ground under it
    const dir = vecNormalize(vecSub(pos, planet.position));
    const below = vecLength(vecSub(pos, planet.position)) <= getSurfaceRadius(planet, dir);
    const touches = vecLength(vecSub(aim, pos)) <= contact || below || state.players.some(p => p.alive && p.teamIndex !== d.teamIndex && p.planetIndex === d.planetIndex
      && vecLength(vecSub(hitboxCenter(p, state.planets, ps.hitboxCenter, ps), pos)) <= contact);
    if (touches) {
      bursts.push({ droneId: d.id, operatorId: d.operatorId, point: pos, planetIndex: d.planetIndex });
      return { ...d, alive: false, end: 'struck' as const, dive: { pos }, strike: { ...d.strike, aim } };
    }
    return { ...d, dive: { pos }, strike: { ...d.strike, aim } };
  });
  return { state: { ...state, drones }, bursts, dived };
}
